import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import CustomerBlock from "./Blocks/CustomerBlock";
import OpportunityBlock from "./Blocks/OpportunityBlock";
import ContactBlock from "./Blocks/ContactBlock";
import { GetContactData, GetOpportunityData, GetStudentDetailsForSoftPhone } from "../../services/SoftphoneService";
import { GetSchoolListCancelToken } from "../../services/RegistrationService";
import { GetStudentDetailsCancelToken } from "../../services/StudentsSettingServices";
import { GetNetsuiteERPBrandNameListCancelToken } from "../../services/BrandServices";
import { GetCampusMgtList } from "../../services/CampusMgtService";
import { IMAGE_URL } from "../../utils/Constants";
import "./CallDashboard.css";

const SalesDashboard = ({ callDetails, encryptedString, phoneNumber, initialMatches = [] }) => {
    const [loading, setLoading] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [customerData, setCustomerData] = useState(callDetails || {});
    const [contactData, setContactData] = useState([]);
    const [opportunityData, setOpportunityData] = useState([]);
    const [schoolMapping, setSchoolMapping] = useState({});
    const [brandMapping, setBrandMapping] = useState({});
    const [campusMapping, setCampusMapping] = useState({});
    const [studentLocationLoading, setStudentLocationLoading] = useState(false);

    // Per-block error state for retry UX
    const [opportunitiesError, setOpportunitiesError] = useState(null);
    const [contactsError, setContactsError] = useState(null);
    const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
    const [contactsLoading, setContactsLoading] = useState(false);

    // Store last payloads for retry
    const lastOppPayloadRef = useRef(null);
    const lastContactPayloadRef = useRef(null);
    const cancelSourceRef = useRef(null);

    // Helper to produce user-friendly error messages
    const friendlyErrorMessage = (err, dataType) => {
        const rawMessage = err?.message || err?.response?.data?.message || '';
        // Common patterns that indicate NetSuite concurrency/timeout
        if (
            rawMessage.includes('500') ||
            rawMessage.toLowerCase().includes('timeout') ||
            rawMessage.toLowerCase().includes('concurrency') ||
            rawMessage.toLowerCase().includes('suitelet') ||
            rawMessage.toLowerCase().includes('restlet')
        ) {
            return `Unable to load ${dataType}. NetSuite may be busy – please retry.`;
        }
        if (rawMessage.toLowerCase().includes('network') || rawMessage.toLowerCase().includes('econnrefused')) {
            return `Network error loading ${dataType}. Please check your connection and retry.`;
        }
        // Fallback
        return `Failed to load ${dataType}. Please retry.`;
    };

    useEffect(() => {
        const source = axios.CancelToken.source();

        GetSchoolListCancelToken(source.token)
            .then((res) => {
                if (res?.status !== 200) return;

                // API returns: { data: [{ value: <slug>, netsuite_department_id: <id>, ... }] }
                const schoolList = Array.isArray(res?.data?.data) ? res.data.data : [];
                const mapping = schoolList.reduce((acc, item) => {
                    const nsDeptId = item?.netsuite_department_id;
                    const slug = item?.value;
                    if (nsDeptId !== undefined && nsDeptId !== null && slug) {
                        acc[String(nsDeptId)] = String(slug);
                    }
                    return acc;
                }, {});

                setSchoolMapping(mapping);
            })
            .catch((err) => {
                if (!axios.isCancel(err)) {
                    // non-fatal: table can still render, but schools may show as '--'
                    // eslint-disable-next-line no-console
                    console.error("CallDashboard -> failed to load school mapping:", err);
                }
            });

        return () => {
            source.cancel("SalesDashboard unmounted");
        };
    }, []);

    useEffect(() => {
        const source = axios.CancelToken.source();

        GetNetsuiteERPBrandNameListCancelToken(source.token)
            .then((res) => {
                if (res?.status !== 200) return;

                // API returns: { data: { items: [{ id, fullname }] } }
                const brandList = Array.isArray(res?.data?.data?.items) ? res.data.data.items : [];
                const mapping = brandList.reduce((acc, item) => {
                    const brandId = item?.id;
                    const brandName = item?.fullname;
                    if (brandId !== undefined && brandId !== null && brandName) {
                        acc[String(brandId)] = String(brandName);
                    }
                    return acc;
                }, {});

                setBrandMapping(mapping);
            })
            .catch((err) => {
                if (!axios.isCancel(err)) {
                    // non-fatal: table can still render, but brands may show as '--'
                    // eslint-disable-next-line no-console
                    console.error("CallDashboard -> failed to load brand mapping:", err);
                }
            });

        return () => {
            source.cancel("SalesDashboard unmounted");
        };
    }, []);

    useEffect(() => {
        const source = axios.CancelToken.source();

        const payload = {
            exportStatus: "false",
            key: "campus_name",
            limit: 100,
            name: "",
            page: 1,
            sort: "DESC"
        };

        GetCampusMgtList(payload, source.token)
            .then((res) => {
                if (res?.status !== 200) return;

                // API returns: { data_list: { data: [{ netsuite_id, campus_name, ... }] } }
                const campusList = Array.isArray(res?.data?.data_list?.data) ? res.data.data_list.data : [];
                const mapping = campusList.reduce((acc, item) => {
                    const netsuiteId = item?.netsuite_id;
                    const campusName = item?.campus_name;
                    const slug = item?.slug;
                    
                    // Map NetSuite ID -> campus_name (primary mapping for opportunity data)
                    if (netsuiteId !== undefined && netsuiteId !== null && campusName) {
                        acc[String(netsuiteId)] = String(campusName);
                    }
                    
                    // Map slug -> campus_name (for legacy support)
                    if (slug && campusName) {
                        acc[String(slug)] = String(campusName);
                    }
                    
                    return acc;
                }, {});

                setCampusMapping(mapping);
            })
            .catch((err) => {
                if (!axios.isCancel(err)) {
                    // non-fatal: table can still render, but campuses may show as '--'
                    // eslint-disable-next-line no-console
                    console.error("CallDashboard -> failed to load campus mapping:", err);
                }
            });

        return () => {
            source.cancel("SalesDashboard unmounted");
        };
    }, []);

    useEffect(() => {
        const studentNumber = customerData?.studentNumber;
        if (!studentNumber) return;

        const source = axios.CancelToken.source();
        let cancelled = false;

        const buildLocation = (student) => {
            // Prefer physical address fields; fall back to postal address
            const parts =
                (student?.ph_city || student?.ph_province || student?.ph_country)
                    ? [student?.ph_city, student?.ph_province, student?.ph_country]
                    : [student?.post_city, student?.post_province, student?.post_country];

            const resolveCountryName = (country) => {
                if (country === undefined || country === null) return "";
                const c = country.toString().trim();
                // If it's a 2-letter region code (ISO-3166-1 alpha-2), expand to full name
                if (/^[a-zA-Z]{2}$/.test(c)) {
                    const code = c.toUpperCase();
                    try {
                        if (typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function") {
                            const dn = new Intl.DisplayNames(["en"], { type: "region" });
                            const name = dn.of(code);
                            if (name) return name;
                        }
                    } catch (_) {
                        // ignore and fall back to code
                    }
                    // Minimal fallback for common cases
                    if (code === "ZA") return "South Africa";
                    return code;
                }
                return c;
            };

            const cleaned = (parts || [])
                .map((p, idx) => {
                    const val = (p ?? "").toString().trim();
                    // country is the 3rd element in our [city, province, country] tuple
                    return idx === 2 ? resolveCountryName(val) : val;
                })
                .filter(Boolean);
            return cleaned.join(", ");
        };

        (async () => {
            try {
                setStudentLocationLoading(true);

                // 1) Resolve portal student id from CRM student number
                const lookupRes = await GetStudentDetailsForSoftPhone(studentNumber, source.token);
                const portalStudentId = lookupRes?.data?.studentData?.id;

                if (!portalStudentId) return;

                // 2) Fetch full student record (includes address fields) and compute display location
                const studentRes = await GetStudentDetailsCancelToken(portalStudentId, source.token);
                const student = studentRes?.data?.data_list;
                const location = buildLocation(student);

                if (cancelled) return;

                setCustomerData((prev) => {
                    // Avoid clobbering if user switched customers while request was inflight
                    if (!prev?.studentNumber || prev.studentNumber !== studentNumber) return prev;
                    const updates = { location };
                    // Pull brand from student profile if not already set
                    if (student?.brand_id && !prev.subsidiary) {
                        updates.subsidiary = String(student.brand_id);
                    }
                    // Pull profile photo from student profile and normalize URL
                    if (student?.photo) {
                        updates.profilePhotoUrl = normalizePhotoUrl(student.photo);
                    }
                    return { ...prev, ...updates };
                });
            } catch (err) {
                if (!axios.isCancel(err)) {
                    // non-fatal
                    // eslint-disable-next-line no-console
                    console.error("CallDashboard -> failed to load student location:", err);
                }
            } finally {
                if (!cancelled) setStudentLocationLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            source.cancel("student location fetch cancelled");
        };
    }, [customerData?.studentNumber]);
    
    useEffect(() => {
        // Only update customerData from props if callDetails actually has content
        // This prevents overwriting local state if callDetails is just {}
        if (callDetails && Object.keys(callDetails).length > 0) {
            setCustomerData(callDetails);
        } else if (!customerData.studentNumber && !initialMatches.length) {
             // Initial load empty state
             setCustomerData({});
        }
        
        if (callDetails && (callDetails.studentNumber || callDetails.customerId)) {
            const source = axios.CancelToken.source();
            cancelSourceRef.current = source;
            
            // Extract customer identifiers similar to registrations
            const customerId = callDetails.customerId || callDetails.studentNumber || '';
            const email = callDetails.email || '';
            const contactID = callDetails.NScontactID || callDetails.contactID || '';
            
            // Only fetch if we have at least customerId or contactID (required by backend)
            if (customerId || contactID) {
                const oppPayload = {
                    number: callDetails.mobile || phoneNumber || callDetails.phone || '',
                    email,
                    studentNumber: callDetails.studentNumber || customerId || '',
                    customerID: customerId || '',
                    ...(encryptedString ? { token: encryptedString } : {})
                };
                const contactPayload = {
                    studentNumber: callDetails.studentNumber || customerId,
                    ...(encryptedString ? { token: encryptedString } : {})
                };

                // Store for retry
                lastOppPayloadRef.current = oppPayload;
                lastContactPayloadRef.current = contactPayload;

                // Reset errors and set per-block loading
                setOpportunitiesError(null);
                setContactsError(null);
                setOpportunitiesLoading(true);
                setContactsLoading(true);
                setLoading(true);

                // Use allSettled so one failure doesn't block the other
                Promise.allSettled([
                    GetContactData(contactPayload, source.token),
                    GetOpportunityData(oppPayload, source.token)
                ]).then(([contactResult, oppResult]) => {
                    // Handle contacts
                    if (contactResult.status === 'fulfilled') {
                        const contactRes = contactResult.value;
                        if (contactRes?.status === 200) {
                            setContactData(Array.isArray(contactRes?.data) ? contactRes.data : []);
                        }
                        setContactsError(null);
                    } else {
                        if (!axios.isCancel(contactResult.reason)) {
                            setContactsError(friendlyErrorMessage(contactResult.reason, 'contacts'));
                        }
                    }
                    setContactsLoading(false);

                    // Handle opportunities
                    if (oppResult.status === 'fulfilled') {
                        const oppRes = oppResult.value;
                        const opportunities = Array.isArray(oppRes?.data) ? oppRes.data : [];
                        setOpportunityData(opportunities);
                        setOpportunitiesError(null);
                    } else {
                        if (!axios.isCancel(oppResult.reason)) {
                            setOpportunitiesError(friendlyErrorMessage(oppResult.reason, 'opportunities'));
                        }
                    }
                    setOpportunitiesLoading(false);

                    setLoading(false);
                });
            } else {
                setLoading(false);
            }
            
            return () => {
                source.cancel('Component unmounted or studentNumber changed');
            };
        } 
        // We DO NOT clear tables here if callDetails is empty, 
        // because we might have loaded them manually via search.
    }, [callDetails, encryptedString, phoneNumber]);

    const handleCustomerSelect = (customer) => {
        const mappedCustomer = {
            ...customer,
            studentNumber: customer.customerId || customer.studentNumber,
            mobile: customer.phone || customer.mobile || phoneNumber,
            deliverymethod: customer.learningMethod || customer.deliverymethod,
            status: customer.stage || customer.status
        };
        setCustomerData(mappedCustomer);
        
        // Load opportunities and contacts for the selected customer
        // Extract customer identifiers similar to registrations
        const customerId = customer.customerId || customer.studentNumber || '';
        const email = customer.email || '';
        const contactID = customer.NScontactID || customer.contactID || '';
        const bookingId = customer.bookingId || '';
        
        // Only fetch if we have at least customerId or contactID (required by backend)
        if (customerId || contactID) {
            const source = axios.CancelToken.source();
            cancelSourceRef.current = source;

            const oppPayload = {
                number: mappedCustomer.mobile || phoneNumber || mappedCustomer.phone || '',
                email,
                studentNumber: mappedCustomer.studentNumber || customerId || '',
                customerID: customerId || '',
                ...(encryptedString ? { token: encryptedString } : {})
            };
            const contactPayload = {
                studentNumber: mappedCustomer.studentNumber,
                ...(encryptedString ? { token: encryptedString } : {})
            };

            // Store for retry
            lastOppPayloadRef.current = oppPayload;
            lastContactPayloadRef.current = contactPayload;

            // Reset errors and set per-block loading
            setOpportunitiesError(null);
            setContactsError(null);
            setOpportunitiesLoading(true);
            setContactsLoading(true);
            setLoading(true);

            Promise.allSettled([
                GetContactData(contactPayload, source.token),
                GetOpportunityData(oppPayload, source.token)
            ]).then(([contactResult, oppResult]) => {
                // Handle contacts
                if (contactResult.status === 'fulfilled') {
                    const contactRes = contactResult.value;
                    if (contactRes?.status === 200) {
                        setContactData(Array.isArray(contactRes?.data) ? contactRes.data : []);
                    }
                    setContactsError(null);
                } else {
                    if (!axios.isCancel(contactResult.reason)) {
                        setContactsError(friendlyErrorMessage(contactResult.reason, 'contacts'));
                    }
                }
                setContactsLoading(false);

                // Handle opportunities
                if (oppResult.status === 'fulfilled') {
                    const oppRes = oppResult.value;
                    const opportunities = Array.isArray(oppRes?.data) ? oppRes.data : [];
                    setOpportunityData(opportunities);
                    setOpportunitiesError(null);
                } else {
                    if (!axios.isCancel(oppResult.reason)) {
                        setOpportunitiesError(friendlyErrorMessage(oppResult.reason, 'opportunities'));
                    }
                }
                setOpportunitiesLoading(false);

                setLoading(false);
            });
        }
    };

    const handleReturnToSearch = () => {
        setCustomerData({});
        // Intentionally NOT clearing contactData and opportunityData
    };

    // Helper to normalize photo path to IMAGE_URL (same convention used elsewhere in portal)
    const normalizePhotoUrl = (rawPath) => {
        if (!rawPath) return null;
        // Convert backslashes to forward slashes
        let path = rawPath.replace(/\\/g, '/');
        // Extract from /Library/... onwards
        const libraryIndex = path.indexOf('/Library/');
        if (libraryIndex !== -1) {
            path = path.substring(libraryIndex);
        }
        // Prefix with IMAGE_URL (no double slashes)
        const base = (IMAGE_URL || '').replace(/\/+$/, '');
        return `${base}${path}`;
    };

    // Retry handlers
    const handleRetryOpportunities = useCallback(() => {
        const payload = lastOppPayloadRef.current;
        if (!payload) return;
        setOpportunitiesError(null);
        setOpportunitiesLoading(true);
        const source = axios.CancelToken.source();
        GetOpportunityData(payload, source.token)
            .then((res) => {
                const opportunities = Array.isArray(res?.data) ? res.data : [];
                setOpportunityData(opportunities);
                setOpportunitiesError(null);
            })
            .catch((err) => {
                if (!axios.isCancel(err)) {
                    setOpportunitiesError(friendlyErrorMessage(err, 'opportunities'));
                }
            })
            .finally(() => setOpportunitiesLoading(false));
    }, []);

    const handleRetryContacts = useCallback(() => {
        const payload = lastContactPayloadRef.current;
        if (!payload) return;
        setContactsError(null);
        setContactsLoading(true);
        const source = axios.CancelToken.source();
        GetContactData(payload, source.token)
            .then((res) => {
                if (res?.status === 200) {
                    setContactData(Array.isArray(res?.data) ? res.data : []);
                }
                setContactsError(null);
            })
            .catch((err) => {
                if (!axios.isCancel(err)) {
                    setContactsError(friendlyErrorMessage(err, 'contacts'));
                }
            })
            .finally(() => setContactsLoading(false));
    }, []);

    return (
        <div className="call-dashboard-wrapper">
            <div className="left-panel-column">
                <CustomerBlock
                    customerData={customerData}
                    debouncedSearch={debouncedSearch}
                    setDebouncedSearch={setDebouncedSearch}
                    loading={loading}
                    brandMapping={brandMapping}
                    campusMapping={campusMapping}
                    opportunityData={opportunityData}
                    onCustomerSelect={handleCustomerSelect}
                    onReturnToSearch={handleReturnToSearch}
                    encryptedString={encryptedString}
                    initialMatches={initialMatches} />
            </div>
            <div className="right-panel  dashboard-hide-small scrollable">
                <OpportunityBlock
                    loading={opportunitiesLoading}
                    opportunityData={opportunityData}
                    schoolMapping={schoolMapping}
                    brandMapping={brandMapping}
                    campusMapping={campusMapping}
                    error={opportunitiesError}
                    onRetry={handleRetryOpportunities} />
                <ContactBlock
                    loading={contactsLoading}
                    contactData={contactData}
                    error={contactsError}
                    onRetry={handleRetryContacts} />
            </div>
        </div>
    );
};

export default SalesDashboard;
